import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { TextField } from '@/design-system/atoms/Forms/TextField';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { useHeroEmbedded } from '@/design-system/contexts/HeroEmbeddedContext';
import { useObserver } from '@/hooks/useObserver';

import styles from './PatientGptBanner.module.scss';

/**
 * PatientGptBanner is the presentational design-system molecule for the 'Got Health Questions?'
 * banner. It is fully controlled — it owns no field value, forms no validity opinion, and has
 * no submit lifecycle. Validation, the submit lifecycle, and navigation stay in the parent
 * features/PatientGptBanner module.
 *
 * Exposes three stable DOM test hooks:
 *   - `data-theme` on the Theme root element
 *   - `data-focus-visible` on the field wrapper div
 *   - `data-animation` ('none' | 'in-view' | 'hover') on the field wrapper div
 */
const PatientGptBanner = ({
    title,
    description,
    ctaLabel,
    ctaAriaLabel,
    ctaIconName,
    inputLabel,
    maxLength,
    theme,
    value,
    onChange,
    onBlur,
    onSubmit,
    isPending,
    isInvalid,
    errorMessage,
    isHeroEmbedded,
}) => {
    // The hero-placement signal can arrive two ways: as a direct prop (Storybook, or any
    // caller that renders this molecule directly) or via HeroEmbeddedContext when the
    // Homepage Hero wraps its banner slot — needed because that slot is an
    // `ahs/components/container` whose boundary a cloneElement prop cannot cross.
    // Read the context unconditionally (Rules of Hooks), then OR with the prop.
    const heroEmbeddedFromContext = useHeroEmbedded();
    const embedded = isHeroEmbedded || heroEmbeddedFromContext;

    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    // Detect prefers-reduced-motion and keep it live as the OS setting changes.
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    });
    React.useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Track visibility for the animate-on-view affordance — live enter/exit so the
    // animation restarts each time the component scrolls back into view (AC-07 / T-07).
    const { ref: fieldRef, inView: isInView } = useObserver();

    // Resolve data-animation per spec §8 precedence:
    //   1. 'none' if reduced-motion OR invalid OR focused
    //   2. 'hover' while hovered
    //   3. 'in-view' while the component is in the viewport
    //   4. 'none'
    const animationState = React.useMemo(() => {
        if (prefersReducedMotion || isInvalid || isFocused) return 'none';
        if (isHovered) return 'hover';
        if (isInView) return 'in-view';

        return 'none';
    }, [prefersReducedMotion, isInvalid, isFocused, isHovered, isInView]);

    const handleFocus = React.useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleBlur = React.useCallback(() => {
        setIsFocused(false);
        onBlur();
    }, [onBlur]);

    const handleMouseEnter = React.useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = React.useCallback(() => {
        setIsHovered(false);
    }, []);

    const submitMethodRef = React.useRef('button');

    const handleButtonMouseDown = React.useCallback(() => {
        submitMethodRef.current = 'button';
    }, []);

    const handleFieldKeyDown = React.useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey && e.nativeEvent?.isComposing !== true) {
            submitMethodRef.current = 'keyboard';
        }
    }, []);

    // Single native submit backs both Enter-in-input and CTA click (spec §6, artifact §J-2).
    const handleSubmit = React.useCallback(
        (e) => {
            e.preventDefault();
            onSubmit(submitMethodRef.current || 'button');
            submitMethodRef.current = 'button';
        },
        [onSubmit],
    );

    const surface = (
        <Theme
            name={theme}
            className={classnames(styles.banner, embedded && styles.embedded)}
            data-testid="patient-gpt-banner"
        >
            <ContentContainer isFullWidth={!embedded}>
                <form
                    aria-label="Health question form"
                    className={styles.form}
                    data-testid="patient-gpt-form"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Title tag="h2" variant="h4" content={title} />
                    {description && (
                        <Text
                            className={styles.description}
                            content={description}
                            allowInlineHTML
                        />
                    )}
                    <div className={styles.formRow}>
                        {/* Field wrapper hosts gradient border, data-focus-visible, data-animation. */}
                        <div
                            ref={fieldRef}
                            className={styles.fieldWrapper}
                            data-animation={animationState}
                            data-focus-visible={isFocused || undefined}
                            data-invalid={isInvalid || undefined}
                            data-testid="patient-gpt-field-wrapper"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <TextField
                                className={styles.textfieldInner}
                                label={inputLabel}
                                value={value}
                                onChange={onChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                onKeyDown={handleFieldKeyDown}
                                isInvalid={isInvalid}
                                validationErrors={errorMessage}
                                isRequired
                                maxLength={maxLength}
                            />
                        </div>
                        <Button
                            className={styles.ctaButton}
                            type="submit"
                            buttonStyle={Button.STYLE.PRIMARY}
                            label={ctaLabel}
                            ariaLabel={ctaAriaLabel || undefined}
                            iconName={ctaIconName}
                            disabled={isPending}
                            onMouseDown={handleButtonMouseDown}
                        />
                    </div>
                </form>
            </ContentContainer>
        </Theme>
    );

    return embedded ? (
        surface
    ) : (
        <ContentContainer data-testid="patient-gpt-banner-container">{surface}</ContentContainer>
    );
};

PatientGptBanner.propTypes = {
    /** H2 title rendered at visual size h4. */
    title: PropTypes.string.isRequired,
    /** Optional rich-text description; trusted AEM-authored HTML sanitized via Text/Typography. */
    description: PropTypes.string,
    /** Visible CTA button label. */
    ctaLabel: PropTypes.string.isRequired,
    /** Accessible-name override for the CTA. Falls back to ctaLabel when empty. */
    ctaAriaLabel: PropTypes.string,
    /** Icon name rendered inside the CTA button. Empty string (default) renders no icon. */
    ctaIconName: PropTypes.string,
    /** The input's accessible name, rendered as a floating label by TextField. */
    inputLabel: PropTypes.string.isRequired,
    /** Applied verbatim as the input's maxLength attribute — structural prevention, not validation. */
    maxLength: PropTypes.number.isRequired,
    /** Resolved theme surface. Already resolved by the Container Adapter before reaching here. */
    theme: Theme.propTypes.name.isRequired,
    /** Current field value; owned by the feature module. Never undefined. */
    value: PropTypes.string.isRequired,
    /** Fired on every keystroke. The molecule stores nothing. */
    onChange: PropTypes.func.isRequired,
    /** Fired on field blur. The molecule forms no validity verdict. */
    onBlur: PropTypes.func.isRequired,
    /** Fired on form submit / CTA activation with the submit method ('button' | 'keyboard'). */
    onSubmit: PropTypes.func.isRequired,
    /** While true: CTA disabled + pending affordance. Repeated activation issues nothing. */
    isPending: PropTypes.bool,
    /** The feature module's validity verdict, forwarded to TextField's controlled isInvalid. */
    isInvalid: PropTypes.bool,
    /** Single user-facing error string for the TextField error slot; null renders no error. */
    errorMessage: PropTypes.string,
    isHeroEmbedded: PropTypes.bool,
};

PatientGptBanner.defaultProps = {
    description: '',
    ctaAriaLabel: '',
    ctaIconName: '',
    isPending: false,
    isInvalid: false,
    errorMessage: null,
    isHeroEmbedded: false,
};

export default PatientGptBanner;
